/* Step 2 of the feed import flow: file upload, parsed list preview, and import button.
   Used for both podcast and RSS feed OPML imports. */

import { useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Upload, Loader2, ArrowLeft, Rss } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ParsedPodcast } from "@/lib/parseOpml";

interface PodcastImportUploadProps {
  parsedPodcasts: ParsedPodcast[];
  parseError: string | null;
  isImporting: boolean;
  onFileSelect: (file: File) => void;
  onImport: () => void;
  onBack: () => void;
  feedLabel?: string;
}

export function PodcastImportUpload({
  parsedPodcasts,
  parseError,
  isImporting,
  onFileSelect,
  onImport,
  onBack,
  feedLabel = "podcast",
}: PodcastImportUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    (file: File) => {
      if (
        file.name.endsWith(".opml") ||
        file.name.endsWith(".xml") ||
        file.type === "text/xml" ||
        file.type === "application/xml" ||
        file.type === "text/x-opml"
      ) {
        onFileSelect(file);
      }
    },
    [onFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const hasPodcasts = parsedPodcasts.length > 0;
  const plural = parsedPodcasts.length !== 1 ? "s" : "";

  return (
    <div className="space-y-4">
      {/* Drop zone / file picker */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border hover:border-muted-foreground/40",
        )}
      >
        <Upload className="size-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground text-center">
          {hasPodcasts
            ? "Drop a different file to replace"
            : "Click to browse or drag & drop your .opml file"}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".opml,.xml"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {/* Parse error */}
      {parseError && (
        <p className="text-destructive text-sm">{parseError}</p>
      )}

      {/* Parsed feeds preview */}
      {hasPodcasts && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {parsedPodcasts.length} {feedLabel}{plural} found
          </p>
          <ScrollArea className="max-h-48 rounded-md border">
            <div className="p-2 space-y-0.5">
              {parsedPodcasts.map((podcast, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                >
                  <Rss className="size-3.5 text-muted-foreground shrink-0" />
                  <span className="truncate">{podcast.name}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} disabled={isImporting}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
        <Button
          onClick={onImport}
          disabled={!hasPodcasts || isImporting}
          className="flex-1"
        >
          {isImporting && <Loader2 className="size-4 animate-spin" />}
          {isImporting
            ? "Importing…"
            : `Import ${parsedPodcasts.length} ${feedLabel}${plural}`}
        </Button>
      </div>
    </div>
  );
}
